import { View } from "react-native";
import { Link } from "expo-router";
import SettingsCard from "../common/settings-card";
import Separator from "../../../common/separator";
import SettingsToggle from "../common/settings-toggle";
import { memo } from "react";
import { useSettingsData } from "../../../../contexts/SettingsDataContext";
import PrepTimeSettings from "./prep-time-settings";
import AllowScheduledOrdersSelector from "./allow-scheduled-orders-selector";
import StyledText from "../../../common/ui/styled-text";
import {
  Calendar,
  Info,
  ShoppingBag,
  ShoppingCart,
  Truck,
  Watch,
} from "lucide-react-native";
import {
  isDeliveryPaymentsEnabled,
  isPickupPaymentsEnabled,
  isPickupTipPromptEnabled,
  getPickupPaymentThreshold,
  getDeliveryPaymentThreshold,
  isPickupPaymentThresholdEnabled,
  isDeliveryPaymentThresholdEnabled,
} from "../../../../utils/setting-helpers";
import { useFeatureFlags } from "../../../../hooks/useFeatureFlags";
import {
  isAgentTypeDelivery,
  isNonStreamOrderingAgent,
} from "../../../../utils/agent-helpers";
import { useActiveAgent } from "../../../../contexts/ActiveAgentContext";
import SwitchToggle from "../../../common/switch-toggle";
import CurrencyInput from "../../../ui/currency-input";

const OrderingSettings = () => {
  const { isPaymentsFeatureEnabled } = useFeatureFlags();
  const {
    settings,
    stripeAccountStatus,
    isTestCallActive,
    handlePickupPaymentsToggle,
    handlePickupTipPromptToggle,
    handlePickupPaymentThresholdChange,
    handlePickupPaymentThresholdToggle,
    handleDeliveryPaymentsToggle,
    handleDeliveryPaymentThresholdChange,
    handleDeliveryPaymentThresholdToggle,
    handleAllowScheduledOrdersToggle,
    handleAllowScheduledOrders,
    handleOrderUntilClosed,
    handleExactTimeEstimate,
  } = useSettingsData();
  const { activeAgent } = useActiveAgent();
  const orderingConfig = settings?.orderingConfig;
  if (!orderingConfig) {
    return null;
  }

  const allowAnonymousCalls = settings?.allowAnonymousCalls ?? false;

  const isDeliveryAgent =
    isAgentTypeDelivery(activeAgent?.type) &&
    orderingConfig.deliveryModuleConfig?.enabled === true;

  const pickupPaymentsEnabled = isPickupPaymentsEnabled(orderingConfig);
  const pickupTipPromptEnabled = isPickupTipPromptEnabled(orderingConfig);
  const pickupPaymentThreshold = getPickupPaymentThreshold(orderingConfig);
  const pickupPaymentThresholdEnabled =
    isPickupPaymentThresholdEnabled(orderingConfig);
  const deliveryPaymentsEnabled = isDeliveryPaymentsEnabled(orderingConfig);
  const deliveryPaymentThreshold = getDeliveryPaymentThreshold(orderingConfig);
  const deliveryPaymentThresholdEnabled =
    isDeliveryPaymentThresholdEnabled(orderingConfig);
  return (
    <div className="space-y-8 pt-4">
      {isPaymentsFeatureEnabled() && stripeAccountStatus === "READY" && (
        <SettingsCard title="Prepay by phone" noSpacing>
          {allowAnonymousCalls && (
            <View className="text-center rounded-md bg-amber-50 border border-amber-200 p-3 mb-4">
              <StyledText className="text-amber-800">
                Unavailable when anonymous calls are enabled.{" "}
                <Link
                  href="/settings?section=call-types"
                  className="text-amber-800 hover:text-amber-900 underline"
                >
                  Click to view
                </Link>
              </StyledText>
            </View>
          )}
          <SettingsToggle
            title="Pickup"
            description="Require phone payment for pickup orders."
            value={pickupPaymentsEnabled}
            onValueChange={handlePickupPaymentsToggle}
            icon={ShoppingBag}
            disabled={isTestCallActive || allowAnonymousCalls}
          />
          {pickupPaymentsEnabled && (
            <View className="mt-6 border-l border-border pl-5 sm:ml-3 space-y-6">
              <View>
                <View className="flex-row items-start justify-between gap-4">
                  <View className="flex-1 min-w-0">
                    <View className="gap-1">
                      <StyledText className="font-medium text-foreground text-sm">
                        Set minimum order amount
                      </StyledText>
                      <StyledText className="text-sm text-muted-foreground">
                        Orders below this amount must pay in person. Orders at
                        or above must pay by phone.
                      </StyledText>
                    </View>
                  </View>
                  <View className="flex-shrink-0">
                    <SwitchToggle
                      value={pickupPaymentThresholdEnabled}
                      onValueChange={handlePickupPaymentThresholdToggle}
                      disabled={isTestCallActive || allowAnonymousCalls}
                    />
                  </View>
                </View>
                {pickupPaymentThresholdEnabled && (
                  <View className="mt-0.5">
                    <CurrencyInput
                      value={pickupPaymentThreshold}
                      onChangeValue={handlePickupPaymentThresholdChange}
                      maxAmount={100000} // $1,000 maximum
                    />
                  </View>
                )}
              </View>

              <View className="flex-row items-start justify-between gap-4">
                <View className="flex-1 min-w-0">
                  <View className="gap-1">
                    <StyledText className="font-medium text-foreground text-sm">
                      Ask for tip
                    </StyledText>
                    <StyledText className="text-sm text-muted-foreground">
                      Prompt customers to add a tip when placing their pickup
                      order.
                    </StyledText>
                  </View>
                </View>
                <View className="flex-shrink-0">
                  <SwitchToggle
                    value={pickupTipPromptEnabled}
                    onValueChange={handlePickupTipPromptToggle}
                    disabled={isTestCallActive || allowAnonymousCalls}
                  />
                </View>
              </View>
            </View>
          )}
          {isDeliveryAgent && (
            <>
              <Separator />
              <SettingsToggle
                title="Delivery"
                description="Require phone payment for delivery orders."
                value={deliveryPaymentsEnabled}
                onValueChange={handleDeliveryPaymentsToggle}
                icon={Truck}
                disabled={isTestCallActive || allowAnonymousCalls}
              />
              {deliveryPaymentsEnabled && (
                <View className="mt-6 border-l border-border pl-5 sm:ml-3 space-y-6">
                  <View>
                    <View className="flex-row items-start justify-between gap-4">
                      <View className="flex-1 min-w-0">
                        <View className="gap-1">
                          <StyledText className="font-medium text-foreground text-sm">
                            Set minimum order amount
                          </StyledText>
                          <StyledText className="text-sm text-muted-foreground">
                            Orders below this amount must pay in person. Orders
                            at or above must pay by phone.
                          </StyledText>
                        </View>
                      </View>
                      <View className="flex-shrink-0">
                        <SwitchToggle
                          value={deliveryPaymentThresholdEnabled}
                          onValueChange={handleDeliveryPaymentThresholdToggle}
                          disabled={isTestCallActive || allowAnonymousCalls}
                        />
                      </View>
                    </View>
                    {deliveryPaymentThresholdEnabled && (
                      <View className="mt-0.5">
                        <CurrencyInput
                          value={deliveryPaymentThreshold}
                          onChangeValue={handleDeliveryPaymentThresholdChange}
                          maxAmount={100000} // $1,000 maximum
                        />
                      </View>
                    )}
                  </View>
                </View>
              )}
              {deliveryPaymentsEnabled && (
                <View className="text-center rounded-md bg-gray-50 border border-gray-200 p-3 opacity-60 flex flex-row items-center gap-2 mt-4">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-gray-800" />
                  <StyledText className="text-gray-800 text-[12px]">
                    Customers are always asked if they'd like to tip their
                    driver for delivery orders.
                  </StyledText>
                </View>
              )}
            </>
          )}
        </SettingsCard>
      )}
      {/* upsell for payments toggle */}
      {isNonStreamOrderingAgent(
        activeAgent?.type,
        settings?.posProvider === "stream"
      ) &&
        isPaymentsFeatureEnabled() && (
          <SettingsCard title="Prepay by phone" noSpacing>
            <SettingsToggle
              title="Pickup"
              description="Require payment before order is placed."
              value={false}
              onValueChange={() => {}}
              icon={ShoppingBag}
              disabled={true}
            />
            <View className="text-center rounded-md bg-amber-50 border border-amber-200 p-3 mt-4">
              <StyledText className="text-amber-800">
                This feature is only available for Stream ordering agents.
              </StyledText>
            </View>
          </SettingsCard>
        )}

      <PrepTimeSettings />

      <SettingsCard title="Order timing" noSpacing>
        <SettingsToggle
          title="Scheduled orders"
          description="Allow customers to place orders for future dates and times."
          value={orderingConfig.allowScheduledOrders !== "never"}
          onValueChange={handleAllowScheduledOrdersToggle}
          icon={Calendar}
          disabled={isTestCallActive}
        />
        {orderingConfig.allowScheduledOrders !== "never" && (
          <View className="mt-4 border-l border-gray-100 pl-3 sm:ml-3">
            <AllowScheduledOrdersSelector
              selectedOption={orderingConfig.allowScheduledOrders}
              onSelect={handleAllowScheduledOrders}
            />
          </View>
        )}

        <Separator />

        <SettingsToggle
          title="Last minute ordering"
          description="Allow orders until close, ignoring prep time."
          value={orderingConfig.orderUntilClosed}
          onValueChange={handleOrderUntilClosed}
          icon={ShoppingCart}
          disabled={isTestCallActive}
        />

        <Separator />

        <SettingsToggle
          title="ETA"
          description="The assistant will give an estimate based on the prep time."
          value={orderingConfig.exactTimeEstimate}
          onValueChange={handleExactTimeEstimate}
          icon={Watch}
          disabled={isTestCallActive}
        />
      </SettingsCard>
    </div>
  );
};

export default memo(OrderingSettings);
